import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { Eye, CheckCircle, Search, ChevronUp, ChevronDown } from "lucide-react";
import { useMsal } from '@azure/msal-react';
import Pagination from '../components/Pagination';

function MyApprovals({ userRole, userEmail }) {
  const userRoles = typeof userRole === 'string' ? userRole.split(',').map(r => r.trim()) : [userRole || 'creator'];
  const hasRole = (role) => userRoles.includes(role);
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState('All');
  const [expenseType, setExpenseType] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'expense_id', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // Track current user's action per expense: 'approved' | 'rejected' | null
  const [myActions, setMyActions] = useState({});
  // Track current user's approval level per expense (number) when available
  const [myLevels, setMyLevels] = useState({});
  // Derived: expense is fully approved if Level 3 row in history is Approved
  const [fullyApprovedMap, setFullyApprovedMap] = useState({});
 
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
 
  useEffect(() => {
    const fetchExpenses = async () => {
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ['User.Read'],
          account: accounts && accounts[0]
        });
        const params = {};
        const headers = { Authorization: `Bearer ${tokenResponse.accessToken}` };
        let url = '/api/expenses';
        if (statusFilter && statusFilter !== 'All') {
          url += `?status=${encodeURIComponent(statusFilter)}`;
        }
        const res = await api.get(url, { headers });
        const data = Array.isArray(res.data) ? res.data : [];
        setExpenses(data);
        // base client-side search filter applied below by effect/useMemo
        setFilteredExpenses(
          searchQuery
            ? data.filter((e) => e.expense_id.toLowerCase().includes(searchQuery.toLowerCase()))
            : data
        );

        // Fetch approval history for each expense to determine current user's action/level
        if (userEmail && data.length) {
          const results = await Promise.all(
            data.map(async (expense) => {
              try {
                const hRes = await api.get(`/api/approvals/history/by`, { params: { expenseId: expense.expense_id } });
                const history = Array.isArray(hRes.data) ? hRes.data : [];
                const norm = (s) => String(s || '').trim().toLowerCase();
                const mine = history.find(h => String(h.approver_email || '').toLowerCase() === String(userEmail).toLowerCase());
                const action = norm(mine?.status) === 'approved' ? 'approved' : (norm(mine?.status) === 'rejected' ? 'rejected' : null);
                const level = mine?.level || null;
                const isRecurring = expense.recurring_type === 'Recurring Expense' || (expense.recurring_type || '').toString().trim().toLowerCase().includes('recurring');
                // Derived fully approved if Level 3 (or Level 1 for recurring) entry is Approved (case-insensitive, tolerant of suffixes)
                const fullyApproved = isRecurring
                  ? history.some(h => Number(h.level) === 1 && norm(h.status).startsWith('approved'))
                  : history.some(h => Number(h.level) === 3 && norm(h.status).startsWith('approved'));
                return [expense.expense_id, { action, level, fullyApproved }];
              } catch (e) {
                console.warn('Failed to fetch history for', expense.expense_id, e);
                return [expense.expense_id, { action: null, level: null, fullyApproved: false }];
              }
            })
          );
          const actionMap = Object.fromEntries(results.map(([id, v]) => [id, v.action]));
          const levelMap = Object.fromEntries(results.map(([id, v]) => [id, v.level]));
          const approvedMap = Object.fromEntries(results.map(([id, v]) => [id, !!v.fullyApproved]));
          setMyActions(actionMap);
          setMyLevels(levelMap);
          setFullyApprovedMap(approvedMap);
        }
      } catch (err) {
        console.error("Failed to load expenses:", err);
      }
    };
    if (accounts && accounts.length) fetchExpenses();
  }, [userEmail, instance, accounts, statusFilter]);
 
  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const filtered = expenses.filter((expense) =>
      expense.expense_id.toLowerCase().includes(query)
    );
    setFilteredExpenses(filtered);
    setCurrentPage(1); // reset page on search
  };

  const requestSort = () => {
    setSortConfig((prev) => ({
      key: 'expense_id',
      direction: prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const getSortIcon = () => {
    return sortConfig.direction === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />;
  };

  // Build filtered + sorted list similar to ExpenseList
  const filteredAndSortedExpenses = useMemo(() => {
    let list = filteredExpenses && filteredExpenses.length ? [...filteredExpenses] : [...expenses];
    // Expense type filter
    if (expenseType !== 'all') {
      list = list.filter((expense) => {
        if (!expense.expense_id) return false;
        const id = expense.expense_id.toUpperCase();
        if (expenseType === 'IT') return id.includes('/IT/');
        if (expenseType === 'Accounts') return id.includes('/ACCOUNT/');
        if (expenseType === 'Admin') return !id.includes('/IT/') && !id.includes('/ACCOUNT/');
        return true;
      });
    }
    // Sort by expense_id
    list.sort((a, b) => {
      const av = String(a.expense_id || '');
      const bv = String(b.expense_id || '');
      if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [expenses, filteredExpenses, expenseType, sortConfig]);

  const totalPages = Math.ceil(filteredAndSortedExpenses.length / itemsPerPage);
  const paginatedExpenses = filteredAndSortedExpenses.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
 
  const handleProceedToPayment = (expenseId) => {
    navigate(`/payment/${encodeURIComponent(expenseId)}`);
  };
 
  const handleViewReport = (expense) => {
    const base = window.location.origin;
    const overall = String(expense.status || '').toLowerCase();
    const isRecurring = expense.recurring_type === 'Recurring Expense' || (expense.recurring_type || '').toString().trim().toLowerCase().includes('recurring');
    let levelParam = 1;
    if (overall === 'approved') {
      levelParam = isRecurring ? 1 : 3;
    } else if (myLevels && myLevels[expense.expense_id]) {
      levelParam = myLevels[expense.expense_id];
    } else if (typeof expense.status === 'string' && expense.status.toLowerCase().startsWith('pending level')) {
      const match = expense.status.match(/Pending Level\s*(\d+)/i);
      if (match && match[1]) levelParam = parseInt(match[1], 10) || 1;
    }
    const url = `${base}/approve/${expense.expense_id}?level=${encodeURIComponent(levelParam)}&email=${encodeURIComponent(userEmail || expense.creator_user_id)}`;
    window.open(url, "_blank");
  };
 
  // removed duplicate pagination variables; using the derived filteredAndSortedExpenses above
 
  return (
    <div className="p-6 w-full space-y-6" style={{ color: 'var(--text)' }}>
      <div>
        <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--heading-color, var(--text))' }}>All Approvals</h1>
        <p className="text-sm" style={{ color: 'var(--muted)' }}>Review and manage approval workflows for expenses</p>
      </div>

      {/* Controls */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
        {/* Search */}
        <div className="relative flex-1 max-w-md w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            placeholder="Search by Expense ID..."
            className="w-full pl-10 pr-4 py-2 text-sm rounded-lg border shadow-sm"
            style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
          />
        </div>

        {/* Filters at End */}
        <div className="flex items-center gap-3 w-full md:w-auto justify-end">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="rounded-lg px-4 py-2 border text-sm shadow-sm"
            style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
          >
            <option value="All">All Status</option>
            <option value="Approved">Approved</option>
            <option value="Pending Level 1">Pending Level 1</option>
            <option value="Pending Level 2">Pending Level 2</option>
            <option value="Pending Level 3">Pending Level 3</option>
            <option value="Rejected">Rejected</option>
            <option value="Payment Done">Payment Done</option>
          </select>

          <select
            value={expenseType}
            onChange={(e) => { setExpenseType(e.target.value); setCurrentPage(1); }}
            className="rounded-lg px-4 py-2 border text-sm shadow-sm"
            style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
          >
            <option value="all">All Types</option>
            <option value="IT">IT Expenses</option>
            <option value="Admin">Admin Expenses</option>
            <option value="Accounts">Accounts Expenses</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="p-6 rounded-lg shadow-sm border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
        <table className="min-w-full text-sm text-left border-separate border-spacing-y-2" style={{ color: 'var(--text)' }}>
          <thead className="border-b" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>
            <tr>
              <th className="p-2 cursor-pointer" onClick={requestSort} title="Sort by Expense ID">
                <div className="flex items-center gap-1">
                  Expense ID
                  {getSortIcon()}
                </div>
              </th>
              <th className="p-2">View</th>
              {/* {userRole !== "approver" && (
                <th className="p-2">Proceed to Payment</th>
              )} */}
            </tr>
          </thead>
          <tbody>
            {paginatedExpenses.map((expense, idx) => {
              return (
                <tr key={idx} className="shadow-sm rounded-md" style={{ background: 'var(--panel)' }}>
                  <td className="p-2 font-semibold text-base" style={{ color: 'var(--text)' }}>
                    {expense.expense_id}
                  </td>
                  <td className="p-2">
                    {(() => {
                      // Button coloring per-user/per-level
                      // - Green if fully approved (L3 done) => visible to everyone
                      // - Else green if THIS user has approved this expense
                      // - Red if rejected
                      // - Otherwise pending color
                      // If derived fully approved, override visual to green
                      const derivedApproved = !!fullyApprovedMap?.[expense.expense_id];
                      const rawStatus = String(expense.status || '');
                      const normStatus = rawStatus.trim().toLowerCase();
                      const overallApproved = derivedApproved || normStatus.includes('approved') || normStatus.includes('payment done');
                      const overallRejected = normStatus.includes('rejected');
                      const myAction = myActions?.[expense.expense_id] || null; // 'approved' | 'rejected' | null
                      const baseBtn = "flex items-center gap-1 px-3 py-2 rounded-md transition text-sm";
                      const classes = `${baseBtn}`;
                      let style;
                      if (overallRejected || myAction === 'rejected') {
                        style = { background: 'rgba(239,68,68,0.15)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.35)', boxShadow: 'var(--shadow-sm)' };
                      } else if (overallApproved || myAction === 'approved') {
                        style = { background: 'rgba(34,197,94,0.15)', color: '#16a34a', border: '1px solid rgba(34,197,94,0.35)', boxShadow: 'var(--shadow-sm)' };
                      } else {
                        style = { background: 'rgba(99,102,241,0.18)', color: '#3730a3', border: '1px solid rgba(99,102,241,0.35)', boxShadow: 'var(--shadow-sm)' };
                      }
                      const label = 'View Report';
                      return (
                        <button
                          onClick={() => handleViewReport(expense)}
                          className={classes}
                          style={style}
                          title={label}
                        >
                          <Eye className="w-4 h-4" />
                          {label}
                        </button>
                      );
                    })()}
                  </td>
                  {!hasRole("approver") && (
                    <td className="p-2">
                      <button
                        onClick={() =>
                          handleProceedToPayment(expense.expense_id)
                        }
                        className="flex items-center gap-1 px-3 py-2 bg-emerald-200 text-emerald-800 rounded hover:bg-emerald-400 transition text-sm"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Proceed
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {paginatedExpenses.length === 0 && (
              <tr>
                <td
                  colSpan={!hasRole("approver") ? "3" : "2"}
                  className="text-center text-gray-500 py-4"
                >
                  No matching expenses found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
 
        {/* Pagination */}
        {filteredAndSortedExpenses.length > 0 && (
          <div className="border-t" style={{ borderColor: 'var(--border)' }}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(p) => setCurrentPage(p)}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
              totalItems={filteredAndSortedExpenses.length}
            />
          </div>
        )}
      </div>
    </div>
  );
}
 
export default MyApprovals;
 






// import { useEffect, useState } from 'react';
// import { useNavigate } from 'react-router-dom';
// import axios from 'axios';
 
// function MyApprovals() {
//   const [expenses, setExpenses] = useState([]);
//   const navigate = useNavigate();
 
//   useEffect(() => {
//     const fetchExpenses = async () => {
//       try {
//         const res = await axios.get('http://localhost:5000/api/expenses?status=Approved');
//         setExpenses(Array.isArray(res.data) ? res.data : []);
//       } catch (err) {
//         console.error('Failed to load expenses:', err);
//       }
//     };
//     fetchExpenses();
//   }, []);
 
//   const handleProceedToPayment = (expenseId) => {
//     navigate(`/payment/${encodeURIComponent(expenseId)}`);
//   };
 
//   const handleViewReport = (expense) => {
//     const url = `http://localhost:5173/approve/${encodeURIComponent(expense.expense_id)}?level=1&email=${encodeURIComponent(expense.creator_user_id)}`;
//     window.open(url, "_blank");
//   };
 
//   return (
//     <div className="container mt-4">
//       <h3>All Approvals</h3>
//       <table className="table table-bordered mt-3">
//         <thead>
//           <tr>
//             <th>Expense ID</th>
//             <th>View</th>
//             <th>Proceed to Payment</th>
//           </tr>
//         </thead>
//         <tbody>
//           {expenses.map((expense, idx) => (
//             <tr key={idx}>
//               <td>{expense.expense_id}</td>
//               <td>
//                 <button className="btn btn-primary btn-sm" onClick={() => handleViewReport(expense)}>
//                   View Report
//                 </button>
//               </td>
//               <td>
//                 <button className="btn btn-success btn-sm" onClick={() => handleProceedToPayment(expense.expense_id)}>


